import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, FilePdf, Table, PencilSimple, DownloadSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import OnlyOfficeEditor from "@/components/office/OnlyOfficeEditor";
import OfficeToolRunner from "@/components/office/OfficeToolRunner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  OFFICE_FORMATS,
  deleteOfficeFile,
  downloadOfficeFile,
  formatOfficeBytes,
  loadOfficeWorkspace,
  uploadOfficeFile,
} from "@/lib/officeTools";

const TYPE_META = {
  docx: { label: "Word", icon: FileText, tone: "text-blue-700 bg-blue-50" },
  xlsx: { label: "Excel", icon: Table, tone: "text-emerald-700 bg-emerald-50" },
  pdf: { label: "PDF", icon: FilePdf, tone: "text-red-700 bg-red-50" },
};

function FileType({ type }) {
  const meta = TYPE_META[type] || TYPE_META.docx;
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium ${meta.tone}`}><Icon size={16} />{meta.label}</span>;
}

export default function OfficeToolsPage() {
  const uploadRef = useRef(null);
  const loadAbortRef = useRef(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [files, setFiles] = useState([]);
  const [state, setState] = useState("loading");
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editorFile, setEditorFile] = useState(null);

  const refresh = useCallback(async () => {
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    try {
      const data = await loadOfficeWorkspace(controller.signal);
      setWorkspaceId(data.workspaceId);
      setCapabilities(data.capabilities);
      setFiles(data.files);
      setState("ready");
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") setState("error");
    } finally {
      if (loadAbortRef.current === controller) loadAbortRef.current = null;
    }
  }, []);

  const closeEditor = useCallback(() => {
    setEditorFile(null);
    refresh();
  }, [refresh]);

  useEffect(() => {
    refresh();
    return () => loadAbortRef.current?.abort();
  }, [refresh]);

  const onChooseFile = (event) => {
    const file = event.target.files?.[0] || null;
    setSelected(file);
    if (file && !title) setTitle(file.name.replace(/\.[^.]+$/, ""));
  };

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!workspaceId || !selected || !title.trim()) return;
    setUploading(true);
    try {
      const created = await uploadOfficeFile(workspaceId, title.trim(), selected);
      setFiles((current) => [created, ...current]);
      setSelected(null);
      setTitle("");
      if (uploadRef.current) uploadRef.current.value = "";
      toast.success("Office file uploaded");
    } catch (error) {
      const message = error?.response?.data?.detail?.message || "Upload failed";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (file) => {
    if (!window.confirm(`Delete “${file.title}”?`)) return;
    try {
      await deleteOfficeFile(workspaceId, file.file_id);
      setFiles((current) => current.filter((item) => item.file_id !== file.file_id));
      toast.success("Office file deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AppShell title="Office Tools" subtitle="WORD · EXCEL · PDF">
      <div className="space-y-6" data-testid="office-tools-page">
        <section className="grid gap-3 sm:grid-cols-3">
          {Object.entries(TYPE_META).map(([type, meta]) => {
            const Icon = meta.icon;
            return (
              <div key={type} className="rounded-lg border border-black/10 p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-md grid place-items-center ${meta.tone}`}><Icon size={24} weight="duotone" /></div>
                <div><div className="font-semibold">{meta.label}</div><div className="text-xs text-neutral-500">Edit in the browser</div></div>
              </div>
            );
          })}
        </section>

        {!capabilities?.enabled && state === "ready" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="alert">
            The Office editor service is not configured yet. File storage remains available, but editing is locked.
          </div>
        )}

        <form onSubmit={submitUpload} className="rounded-lg border border-black/10 p-4 grid gap-3 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
          <label className="text-sm font-medium">Title
            <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={180} className="mt-1" placeholder="Document title" />
          </label>
          <label className="text-sm font-medium">DOCX, XLSX, or PDF
            <Input ref={uploadRef} type="file" accept={OFFICE_FORMATS.map((item) => `.${item}`).join(",")} onChange={onChooseFile} className="mt-1" />
          </label>
          <Button type="submit" disabled={uploading || !selected || !title.trim()} className="h-10">
            <UploadSimple size={16} className="mr-2" />{uploading ? "Uploading…" : "Upload"}
          </Button>
        </form>

        {state === "loading" && <div className="py-16 text-center text-sm text-neutral-500">Loading Office files…</div>}
        {state === "error" && <div className="py-16 text-center text-sm text-red-700">Office files could not be loaded.</div>}
        {state === "ready" && files.length === 0 && (
          <div className="rounded-lg border border-dashed border-black/15 py-16 text-center">
            <div className="font-medium">No Office files yet</div>
            <p className="mt-1 text-sm text-neutral-500">Upload a Word, Excel, or PDF file to begin.</p>
          </div>
        )}
        {state === "ready" && files.length > 0 && (
          <div className="grid gap-3">
            {files.map((file) => (
              <article key={file.file_id} className="rounded-lg border border-black/10 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="office-file-row">
                <FileType type={file.file_type} />
                <div className="min-w-0 flex-1">
                  <h2 className="font-medium truncate" style={{ unicodeBidi: "plaintext" }}>{file.title}</h2>
                  <div className="text-xs text-neutral-500 truncate" style={{ unicodeBidi: "plaintext" }}>
                    {file.original_filename} · {formatOfficeBytes(file.size_bytes)} · v{file.version}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={() => setEditorFile(file)} disabled={!capabilities?.editing} className="h-9">
                    <PencilSimple size={15} className="mr-1.5" />Edit
                  </Button>
                  <Button type="button" variant="outline" onClick={() => downloadOfficeFile(workspaceId, file).catch(() => toast.error("Download failed"))} className="h-9">
                    <DownloadSimple size={15} className="mr-1.5" />Download
                  </Button>
                  <Button type="button" variant="outline" onClick={() => removeFile(file)} className="h-9 text-red-700">
                    <Trash size={15} aria-hidden="true" /><span className="sr-only">Delete {file.title}</span>
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
        {state === "ready" && workspaceId && (
          <OfficeToolRunner workspaceId={workspaceId} capabilities={capabilities} />
        )}
      </div>
      {editorFile && (
        <OnlyOfficeEditor
          workspaceId={workspaceId}
          file={editorFile}
          onClose={closeEditor}
        />
      )}
    </AppShell>
  );
}
